// views/Geradoras/geradorasSeries.ts
//
// Lógica pura da aba "Análise" de Geradoras: janelas de período, normalização
// por kWh/kWp, resumo de série, comparação entre dois períodos e as quatro
// réguas de "esperado". Sem React e sem Supabase de propósito — é o pedaço que
// erra silencioso se não for testado (node --test).
//
// A série vem da RPC get_geracao_series (migration 20260827235910), que já
// resolve a parte perigosa no Postgres: geração = acumulado no fim do balde
// menos o fim do balde anterior. Aqui nunca se soma acumulador.

export type Granularidade = 'hora' | 'dia' | 'mes';

/** Régua do "esperado". Todas as quatro convivem: a tela deixa escolher. */
export type ModoReferencia = 'irmas' | 'propria' | 'meta' | 'ano_anterior';

export interface PontoSerie {
    site_id_api: string;
    /** ISO com fuso — a RPC devolve timestamptz. */
    bucket: string;
    /** null = acumulador resetou ou não havia baseline. Buraco, não zero. */
    geracao_kwh: number | null;
    potencia_media_w: number | null;
    potencia_pico_w: number | null;
    amostras: number;
}

export interface Janela {
    inicio: Date;
    fim: Date;
    label: string;
}

export interface MetaGeradora {
    site_id_api: string;
    /** 1-12 */
    mes: number;
    /** Normalizado sobre `pnom_kwp_ref`, NÃO sobre capacidade_instalada. Ver metaNoPeriodo. */
    kwh_por_kwp: number;
    /** Energia esperada do bloco no mês (kWh, P50). Preferir esta: não depende do cadastro. */
    kwh_esperado?: number | null;
    kwh_esperado_p90?: number | null;
    pnom_kwp_ref?: number | null;
    fonte?: string | null;
}

/** Uma linha de get_geracao_vs_meta: realizado x esperado de um mês. */
export interface PontoVsMeta {
    site_id_api: string;
    /** 1º dia do mês (ISO, fuso local). */
    mes_ref: string;
    geracao_kwh: number | null;
    dias_com_dado: number;
    dias_no_mes: number;
    /** Fracionário: o dia corrente entra proporcionalmente. */
    dias_decorridos: number;
    /** Havia leitura antes do mês — o primeiro delta tem de onde partir. */
    tem_baseline: boolean;
    /** Valor corrigido à mão em Config > Geração e Metas: vence sobre tudo. */
    ajuste_manual?: boolean;
    /** Dias com delta negativo (contador resetou). Único caso de energia perdida. */
    baldes_nulos: number;
    /** Meta já rateada pelos dias decorridos. */
    meta_kwh: number | null;
    meta_kwh_p90: number | null;
    /** Meta do mês inteiro, sem rateio. */
    meta_cheia_kwh: number | null;
}

// ---------------- Períodos ----------------

export type PresetPeriodo =
    | 'hoje' | 'ontem' | '7d' | '30d' | 'mes_atual' | 'mes_anterior' | 'ano_atual' | '12m';

export const PRESET_LABEL: Record<PresetPeriodo, string> = {
    hoje: 'Hoje',
    ontem: 'Ontem',
    '7d': 'Últimos 7 dias',
    '30d': 'Últimos 30 dias',
    mes_atual: 'Mês atual',
    mes_anterior: 'Mês anterior',
    ano_atual: 'Ano atual',
    '12m': 'Últimos 12 meses',
};

const MES_CURTO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const inicioDoDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const somarDias = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/**
 * Janela [inicio, fim) — fim é exclusivo, igual à RPC. "Hoje" vai até o começo
 * de amanhã para não cortar as horas que ainda vão chegar.
 */
export function janelaDoPreset(preset: PresetPeriodo, agora: Date = new Date()): Janela {
    const hoje = inicioDoDia(agora);
    switch (preset) {
        case 'hoje':
            return { inicio: hoje, fim: somarDias(hoje, 1), label: 'Hoje' };
        case 'ontem':
            return { inicio: somarDias(hoje, -1), fim: hoje, label: 'Ontem' };
        case '7d':
            return { inicio: somarDias(hoje, -6), fim: somarDias(hoje, 1), label: 'Últimos 7 dias' };
        case '30d':
            return { inicio: somarDias(hoje, -29), fim: somarDias(hoje, 1), label: 'Últimos 30 dias' };
        case 'mes_atual': {
            const ini = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
            return { inicio: ini, fim: somarDias(hoje, 1), label: `${MES_CURTO[ini.getMonth()]}/${ini.getFullYear()}` };
        }
        case 'mes_anterior': {
            const ini = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
            const fim = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
            return { inicio: ini, fim, label: `${MES_CURTO[ini.getMonth()]}/${ini.getFullYear()}` };
        }
        // Os dois presets longos existem para o painel "Esperado x realizado":
        // a meta do PVsyst é mensal e sazonal, e só faz sentido lida ao longo
        // do ano. Com o período máximo de 30 dias que existia antes, o painel
        // nunca teria mais de dois pontos.
        case 'ano_atual': {
            const ini = new Date(hoje.getFullYear(), 0, 1);
            return { inicio: ini, fim: somarDias(hoje, 1), label: String(hoje.getFullYear()) };
        }
        case '12m': {
            const ini = new Date(hoje.getFullYear(), hoje.getMonth() - 11, 1);
            return { inicio: ini, fim: somarDias(hoje, 1), label: 'Últimos 12 meses' };
        }
    }
}

export type ModoComparacao = 'anterior' | 'ano_anterior' | 'nenhum';

/**
 * Período de contraste. 'anterior' desloca a janela pelo seu próprio tamanho
 * (7 dias comparam com os 7 anteriores); 'ano_anterior' volta 1 ano civil.
 *
 * ⚠️ 'ano_anterior' só tem dado para a SAJ hoje (telemetria desde 01/12/2025).
 * As Chint só terão em 05/03/2027 e as Growatt em 25/07/2027 — a tela precisa
 * mostrar "sem histórico" em vez de zero. Ver geradorasSeries.temCobertura.
 */
export function janelaComparacao(base: Janela, modo: ModoComparacao): Janela | null {
    if (modo === 'nenhum') return null;
    if (modo === 'ano_anterior') {
        const inicio = new Date(base.inicio); inicio.setFullYear(inicio.getFullYear() - 1);
        const fim = new Date(base.fim); fim.setFullYear(fim.getFullYear() - 1);
        return { inicio, fim, label: `${base.label} de ${inicio.getFullYear()}` };
    }
    const duracao = base.fim.getTime() - base.inicio.getTime();
    return {
        inicio: new Date(base.inicio.getTime() - duracao),
        fim: new Date(base.inicio.getTime()),
        label: 'Período anterior',
    };
}

/** Granularidade sensata para a janela: dia curto vira hora, mês longo vira mês. */
export function granularidadeSugerida(janela: Janela): Granularidade {
    const dias = (janela.fim.getTime() - janela.inicio.getTime()) / 86_400_000;
    if (dias <= 3) return 'hora';
    if (dias <= 120) return 'dia';
    return 'mes';
}

// ---------------- Normalização ----------------

/** capacidade_instalada é MWp no cadastro; kWp é a unidade que compara usinas. */
export const capacidadeKwp = (capacidadeMwp: number | null | undefined): number | null =>
    capacidadeMwp && capacidadeMwp > 0 ? capacidadeMwp * 1000 : null;

/**
 * kWh/kWp — a única forma honesta de comparar uma usina de 1,1 MWp com uma de
 * 77 kWp. É o que transforma "gerou mais" em "gerou melhor".
 */
export function kwhPorKwp(kwh: number | null, capacidadeMwp: number | null | undefined): number | null {
    const kwp = capacidadeKwp(capacidadeMwp);
    if (kwh == null || !kwp) return null;
    return kwh / kwp;
}

// ---------------- Resumo de série ----------------

export interface ResumoSerie {
    totalKwh: number;
    /** Baldes que realmente tinham dado (geracao_kwh não-null). */
    baldesComDado: number;
    /** Média por balde com dado — não pelo total de baldes, senão buraco vira queda. */
    mediaKwh: number | null;
    melhor: { bucket: string; kwh: number } | null;
    pior: { bucket: string; kwh: number } | null;
    picoPotenciaW: number | null;
    kwhKwp: number | null;
}

export function resumirSerie(pontos: PontoSerie[], capacidadeMwp?: number | null): ResumoSerie {
    let total = 0, comDado = 0, pico: number | null = null;
    let melhor: { bucket: string; kwh: number } | null = null;
    let pior: { bucket: string; kwh: number } | null = null;

    for (const p of pontos) {
        if (p.potencia_pico_w != null) pico = pico == null ? p.potencia_pico_w : Math.max(pico, p.potencia_pico_w);
        if (p.geracao_kwh == null) continue;
        total += p.geracao_kwh;
        comDado++;
        if (!melhor || p.geracao_kwh > melhor.kwh) melhor = { bucket: p.bucket, kwh: p.geracao_kwh };
        if (!pior || p.geracao_kwh < pior.kwh) pior = { bucket: p.bucket, kwh: p.geracao_kwh };
    }

    return {
        totalKwh: total,
        baldesComDado: comDado,
        mediaKwh: comDado > 0 ? total / comDado : null,
        melhor,
        pior,
        picoPotenciaW: pico,
        kwhKwp: kwhPorKwp(comDado > 0 ? total : null, capacidadeMwp),
    };
}

export interface Comparacao {
    deltaKwh: number;
    deltaPct: number | null;
    /** false quando o período de contraste não tem cobertura — não é queda de 100%. */
    comparavel: boolean;
}

/**
 * ⚠️ Base zero ou sem balde com dado devolve comparavel:false. Sem isso, uma
 * usina que só começou a reportar mês passado apareceria como "+∞%", e uma sem
 * histórico apareceria como "-100%" — os dois mentem.
 */
export function compararResumos(atual: ResumoSerie, base: ResumoSerie): Comparacao {
    const comparavel = base.baldesComDado > 0 && base.totalKwh > 0;
    return {
        deltaKwh: atual.totalKwh - base.totalKwh,
        deltaPct: comparavel ? ((atual.totalKwh - base.totalKwh) / base.totalKwh) * 100 : null,
        comparavel,
    };
}

// ---------------- Agrupamento ----------------

export function agruparPorSite(pontos: PontoSerie[]): Record<string, PontoSerie[]> {
    const mapa: Record<string, PontoSerie[]> = {};
    for (const p of pontos) (mapa[p.site_id_api] ||= []).push(p);
    for (const lista of Object.values(mapa)) {
        lista.sort((a, b) => new Date(a.bucket).getTime() - new Date(b.bucket).getTime());
    }
    return mapa;
}

// ---------------- Régua do "esperado" ----------------

export interface ContextoReferencia {
    /** kWh/kWp de cada usina no MESMO período (para a régua 'irmas'). */
    kwhKwpPorSite: Record<string, number | null>;
    /** kWh/kWp da própria usina no período de contraste (régua 'propria'/'ano_anterior'). */
    kwhKwpContraste?: number | null;
    metas?: MetaGeradora[];
    mesDeReferencia?: number; // 1-12
    /**
     * kWp de cada usina COMO ESTÁ NO CADASTRO. A régua 'meta' precisa disto
     * para falar a mesma língua do ranking — ver metaNoPeriodo.
     */
    capacidadeKwpPorSite?: Record<string, number | null>;
    /** Dias cobertos pela janela selecionada (fracionário no dia corrente). */
    diasDoPeriodo?: number;
    /** Dias do mês de referência — denominador do rateio da meta mensal. */
    diasDoMesDeReferencia?: number;
}

export const mediana = (valores: number[]): number | null => {
    const v = valores.filter(x => Number.isFinite(x)).sort((a, b) => a - b);
    if (v.length === 0) return null;
    const meio = Math.floor(v.length / 2);
    return v.length % 2 ? v[meio] : (v[meio - 1] + v[meio]) / 2;
};

/**
 * Valor esperado de kWh/kWp para uma usina, na régua escolhida.
 * Devolve null quando a régua não tem base — a tela mostra "sem referência"
 * em vez de inventar um número.
 *
 * 'irmas' usa a MEDIANA das outras usinas, não a média: uma usina parada
 * (Vinicius Cândido, zerada desde 30/01/2026) ou com acumulador corrompido
 * (Qgbt1) puxaria a média e afrouxaria a régua justamente quando ela precisa
 * apertar.
 */
export function referenciaEsperada(
    site: string,
    modo: ModoReferencia,
    ctx: ContextoReferencia,
): number | null {
    if (modo === 'irmas') {
        const outras = Object.entries(ctx.kwhKwpPorSite)
            .filter(([s, v]) => s !== site && v != null && v > 0)
            .map(([, v]) => v as number);
        return outras.length >= 2 ? mediana(outras) : null;
    }
    if (modo === 'propria' || modo === 'ano_anterior') {
        return ctx.kwhKwpContraste != null && ctx.kwhKwpContraste > 0 ? ctx.kwhKwpContraste : null;
    }
    return metaNoPeriodo(site, ctx);
}

/**
 * Meta do PVsyst convertida para a MESMA base que o ranking usa no realizado.
 *
 * ⚠️ Esta conversão não é firula. `kwh_por_kwp` da meta é normalizado pela Pnom
 * DC do laudo (Morrinhos: 6.121 kWp), enquanto o ranking divide a geração real
 * por `unidades_geradoras.capacidade_instalada` (os mesmos 5 blocos: 4.950
 * kWp). Comparar os dois direto — que é o que a tela fazia — infla o realizado
 * em 24% e faz uma usina 7% ABAIXO do laudo aparecer como "acima da meta".
 *
 * Por isso partimos de `kwh_esperado` (kWh absolutos, imunes ao denominador) e
 * dividimos pela capacidade DO CADASTRO. `kwh_por_kwp` fica só como fallback
 * para meta antiga, cadastrada à mão antes desta coluna existir.
 *
 * O rateio por dias existe porque a meta é MENSAL: comparar 7 dias de geração
 * com a meta do mês inteiro reprovaria todo mundo.
 */
export function metaNoPeriodo(site: string, ctx: ContextoReferencia): number | null {
    const meta = (ctx.metas || []).find(m => m.site_id_api === site && m.mes === ctx.mesDeReferencia);
    if (!meta) return null;

    const kwpCadastro = ctx.capacidadeKwpPorSite?.[site];
    if (meta.kwh_esperado == null || !kwpCadastro) return meta.kwh_por_kwp;

    const dias = ctx.diasDoPeriodo;
    const noMes = ctx.diasDoMesDeReferencia;
    const proporcao = dias != null && noMes != null && noMes > 0 ? Math.min(dias / noMes, 1) : 1;

    return (meta.kwh_esperado * proporcao) / kwpCadastro;
}

export type StatusDesvio = 'acima' | 'esperado' | 'abaixo' | 'critico' | 'sem_referencia';

/**
 * Faixas: ±5% é ruído de clima/sujeira entre usinas do mesmo terreno; até -15%
 * é "abaixo"; abaixo disso é crítico. Calibrado no espalhamento real dos 5
 * blocos Chint, que andam a poucos pontos um do outro no mesmo dia.
 */
export function classificarDesvio(real: number | null, esperado: number | null): StatusDesvio {
    if (real == null || esperado == null || esperado <= 0) return 'sem_referencia';
    const pct = ((real - esperado) / esperado) * 100;
    if (pct >= 5) return 'acima';
    if (pct >= -5) return 'esperado';
    if (pct >= -15) return 'abaixo';
    return 'critico';
}

export const desvioPercentual = (real: number | null, esperado: number | null): number | null =>
    real == null || esperado == null || esperado <= 0 ? null : ((real - esperado) / esperado) * 100;

// ---------------- Confiança do dado ----------------

export interface SinalDeDado {
    site_id_api: string;
    tipo: 'sem_dado' | 'amostragem_baixa' | 'acumulador_travado' | 'zerada';
    detalhe: string;
    baldesAfetados: number;
}

/**
 * Diagnóstico de qualidade da série. Existe porque o dado bruto tem defeitos
 * reais e silenciosos: em 27/08/2026 a Qgbt1 estava com o acumulador travado
 * desde as 16h25 do dia anterior e a Vinicius Cândido zerada há 5 dias — e
 * nenhuma das duas aparecia como problema na tela.
 *
 * `amostragem_baixa` compara com a MEDIANA de amostras da própria usina, não
 * com um número fixo: a cadência varia por fabricante (Chint 5min => ~12
 * amostras/hora, Growatt 10min => ~6).
 */
export function detectarSinais(pontos: PontoSerie[], gran: Granularidade): SinalDeDado[] {
    const sinais: SinalDeDado[] = [];
    for (const [site, lista] of Object.entries(agruparPorSite(pontos))) {
        const comDado = lista.filter(p => p.geracao_kwh != null);
        if (comDado.length === 0) {
            sinais.push({ site_id_api: site, tipo: 'sem_dado', detalhe: 'Nenhum balde com leitura no período', baldesAfetados: lista.length });
            continue;
        }

        const total = comDado.reduce((s, p) => s + (p.geracao_kwh || 0), 0);
        if (total === 0) {
            sinais.push({ site_id_api: site, tipo: 'zerada', detalhe: `Geração zero em todo o período (${comDado.length} baldes)`, baldesAfetados: comDado.length });
            continue;
        }

        const travados = lista.filter(p => p.geracao_kwh === null).length;
        if (travados > 0) {
            sinais.push({ site_id_api: site, tipo: 'acumulador_travado', detalhe: `${travados} balde(s) sem geração calculável (acumulador resetou ou não avançou)`, baldesAfetados: travados });
        }

        if (gran === 'hora') {
            const med = mediana(lista.map(p => p.amostras));
            if (med && med > 1) {
                const fracos = lista.filter(p => p.amostras < med / 2).length;
                if (fracos > 0) {
                    sinais.push({ site_id_api: site, tipo: 'amostragem_baixa', detalhe: `${fracos} hora(s) com menos da metade das amostras usuais (${med} por hora)`, baldesAfetados: fracos });
                }
            }
        }
    }
    return sinais;
}

// ---------------- Formatação de eixo ----------------

const TZ = 'America/Sao_Paulo';

/** Rótulo do balde no eixo X, conforme a granularidade. */
export function rotuloBucket(iso: string, gran: Granularidade): string {
    const d = new Date(iso);
    if (gran === 'hora') return d.toLocaleTimeString('pt-BR', { hour: '2-digit', timeZone: TZ }) + 'h';
    if (gran === 'dia') return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: TZ });
    return `${MES_CURTO[Number(d.toLocaleString('en-US', { month: 'numeric', timeZone: TZ })) - 1]}/${d.toLocaleString('en-US', { year: '2-digit', timeZone: TZ })}`;
}

/** Chave de alinhamento entre dois períodos de tamanhos iguais (posição no eixo). */
export function alinharPorPosicao(a: PontoSerie[], b: PontoSerie[]): { chave: string; a: number | null; b: number | null }[] {
    const n = Math.max(a.length, b.length);
    const saida: { chave: string; a: number | null; b: number | null }[] = [];
    for (let i = 0; i < n; i++) {
        saida.push({
            chave: a[i]?.bucket ?? b[i]?.bucket ?? String(i),
            a: a[i]?.geracao_kwh ?? null,
            b: b[i]?.geracao_kwh ?? null,
        });
    }
    return saida;
}

// ---------------- Esperado x realizado (PVsyst) ----------------

/** Dias do mês civil. Fevereiro bissexto incluso — `new Date(a, m, 0)` resolve. */
export const diasNoMes = (ano: number, mes1a12: number): number =>
    new Date(ano, mes1a12, 0).getDate();

/** Dias cobertos pela janela, fracionários, sem passar do instante atual. */
export function diasDaJanela(janela: Janela, agora: Date = new Date()): number {
    const fim = Math.min(janela.fim.getTime(), agora.getTime());
    return Math.max(0, (fim - janela.inicio.getTime()) / 86_400_000);
}

export interface MesVsMeta {
    /** ISO do 1º dia do mês. */
    mesRef: string;
    realKwh: number;
    /** Meta rateada pelos dias decorridos. null = usina sem meta cadastrada. */
    metaKwh: number | null;
    metaP90Kwh: number | null;
    /** Meta do mês fechado, sem rateio — o número do laudo. */
    metaCheiaKwh: number | null;
    atingimentoPct: number | null;
    /** true quando a janela não cobre o mês inteiro (mês corrente, ou borda da seleção). */
    parcial: boolean;
    /**
     * Dias sem coleta. É saúde do COLETOR, não energia perdida — ver `comparavel`.
     * Fica na tela como sinal para o time de TI e não entra em conta nenhuma.
     */
    diasSemDado: number;
    /** Valor corrigido à mão em Config > Geração e Metas. A tela declara isso. */
    ajustadoManual: boolean;
    /**
     * Mês em que dá para julgar a usina.
     *
     * ⚠️ Dia sem coleta NÃO torna o mês incomparável, ao contrário do que esta
     * tela assumiu na primeira versão. O contador do inversor é ACUMULATIVO:
     * quando o coletor volta, o delta do dia de retorno traz junto tudo que foi
     * gerado no intervalo mudo. Conferido em 04/09/2026 nos cinco blocos de
     * Morrinhos — a soma dos deltas bate com a diferença bruta do acumulado em
     * 0,007% de abril a agosto (1 a 63 kWh em ~800 MWh).
     *
     * O que realmente quebra a comparação é outra coisa:
     *   - sem baseline: não há leitura anterior ao mês, então o primeiro delta
     *     não existe e os primeiros dias somem (março/2026: a telemetria de
     *     Morrinhos só começou em 05/03);
     *   - contador resetado: delta negativo vira NULL e aquela energia se perde
     *     de fato.
     */
    comparavel: boolean;
    usinas: number;
}

/** Um bloco/usina consolidado no período — responde "qual deles está puxando para baixo". */
export interface UsinaVsMeta {
    site_id_api: string;
    realKwh: number;
    metaKwh: number;
    /** Só dos meses comparáveis desta usina. É o percentual que a tela mostra. */
    atingimentoPct: number | null;
    mesesComparaveis: number;
    diasSemDado: number;
}

export interface ResumoVsMeta {
    meses: MesVsMeta[];
    totalRealKwh: number;
    totalMetaKwh: number;
    totalMetaP90Kwh: number;
    /**
     * O ÚNICO percentual da tela, e ele conta SÓ os meses comparáveis.
     *
     * Antes existiam dois — o agregado cheio e o dos meses limpos — e o cheio,
     * que era o errado, ficava em destaque. Medido em Morrinhos em 03/09/2026:
     * 82,4% no cheio contra 93% nos limpos, porque 405 dos 1.018 MWh de déficit
     * eram 13 dias sem leitura, não energia perdida. Um número errado grande ao
     * lado de um certo pequeno é pior que um número só.
     *
     * Mesma postura do "Não comparável" da conciliação Q-07: registro sem
     * condição de julgamento fica FORA da conta e aparece marcado, em vez de
     * contaminar a média.
     */
    atingimentoPct: number | null;
    mesesComparaveis: number;
    /** Meses excluídos do percentual por dia mudo. Aparecem na tabela, marcados. */
    mesesForaDaConta: number;
    /** Consolidado por bloco, para achar qual usina puxa o conjunto para baixo. */
    porUsina: UsinaVsMeta[];
    /** Sites da seleção que têm meta cadastrada. O painel só fala destes. */
    sitesComMeta: string[];
    /** Algum mês teve dia sem coleta. Sinal para TI; não afeta os números. */
    temLacuna: boolean;
    /** Algum mês do recorte foi corrigido à mão — a tela declara isso. */
    temAjusteManual: boolean;
}

/**
 * Consolida as linhas de get_geracao_vs_meta em série mensal + total.
 *
 * ⚠️ Linha SEM meta (`meta_kwh` null) entra no total de realizado? NÃO — e nem
 * no de meta. Somar a geração de uma usina de 77 kWp sem laudo contra uma meta
 * que só existe para os 5 blocos de Morrinhos produziria um atingimento
 * inventado e alto. O painel declara quantas usinas da seleção ele cobre.
 *
 * `parcial` é por mês e não por período: num "ano atual" só o mês corrente é
 * parcial, e marcar o ano inteiro esconderia a informação útil.
 */
export function resumirVsMeta(linhas: PontoVsMeta[]): ResumoVsMeta {
    const comMeta = linhas.filter(l => l.meta_kwh != null && l.meta_kwh > 0);

    const porMes = new Map<string, PontoVsMeta[]>();
    for (const l of comMeta) {
        const chave = String(l.mes_ref).slice(0, 10);
        const lista = porMes.get(chave);
        if (lista) lista.push(l); else porMes.set(chave, [l]);
    }

    const meses: MesVsMeta[] = [...porMes.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([mesRef, ls]) => {
            const realKwh = ls.reduce((s, l) => s + (l.geracao_kwh ?? 0), 0);
            const metaKwh = ls.reduce((s, l) => s + (l.meta_kwh ?? 0), 0);
            const metaP90Kwh = ls.reduce((s, l) => s + (l.meta_kwh_p90 ?? 0), 0);
            const metaCheiaKwh = ls.reduce((s, l) => s + (l.meta_cheia_kwh ?? 0), 0);
            // Do PIOR bloco: basta um dos cinco furado para o mês do conjunto ficar.
            const diasSemDado = Math.max(
                0,
                ...ls.map(l => Math.round(l.dias_decorridos) - l.dias_com_dado),
            );
            return {
                mesRef,
                realKwh,
                metaKwh,
                metaP90Kwh,
                metaCheiaKwh,
                atingimentoPct: metaKwh > 0 ? (realKwh / metaKwh) * 100 : null,
                parcial: ls.some(l => l.dias_decorridos < l.dias_no_mes - 0.01),
                diasSemDado,
                comparavel: ls.every(l => l.tem_baseline && (l.baldes_nulos ?? 0) === 0),
                ajustadoManual: ls.some(l => l.ajuste_manual === true),
                usinas: ls.length,
            };
        });

    const comparaveis = meses.filter(m => m.comparavel);
    const realComparavel = comparaveis.reduce((s, m) => s + m.realKwh, 0);
    const metaComparavel = comparaveis.reduce((s, m) => s + m.metaKwh, 0);

    // ---- consolidado por usina, com a mesma régua de comparabilidade ----
    const porSite = new Map<string, PontoVsMeta[]>();
    for (const l of comMeta) {
        const lista = porSite.get(l.site_id_api);
        if (lista) lista.push(l); else porSite.set(l.site_id_api, [l]);
    }

    const porUsina: UsinaVsMeta[] = [...porSite.entries()].map(([site, ls]) => {
        const limpos = ls.filter(l => l.tem_baseline && (l.baldes_nulos ?? 0) === 0);
        const real = limpos.reduce((s, l) => s + (l.geracao_kwh ?? 0), 0);
        const meta = limpos.reduce((s, l) => s + (l.meta_kwh ?? 0), 0);
        return {
            site_id_api: site,
            // Os absolutos são de TODOS os meses: é a energia que entrou de verdade.
            realKwh: ls.reduce((s, l) => s + (l.geracao_kwh ?? 0), 0),
            metaKwh: ls.reduce((s, l) => s + (l.meta_kwh ?? 0), 0),
            // O percentual, não: segue a mesma régua do agregado.
            atingimentoPct: meta > 0 ? (real / meta) * 100 : null,
            mesesComparaveis: limpos.length,
            diasSemDado: ls.reduce((s, l) => s + Math.max(0, Math.round(l.dias_decorridos) - l.dias_com_dado), 0),
        };
    }).sort((a, b) => (a.atingimentoPct ?? 999) - (b.atingimentoPct ?? 999));

    return {
        meses,
        porUsina,
        totalRealKwh: meses.reduce((s, m) => s + m.realKwh, 0),
        totalMetaKwh: meses.reduce((s, m) => s + m.metaKwh, 0),
        totalMetaP90Kwh: meses.reduce((s, m) => s + m.metaP90Kwh, 0),
        atingimentoPct: metaComparavel > 0 ? (realComparavel / metaComparavel) * 100 : null,
        mesesComparaveis: comparaveis.length,
        mesesForaDaConta: meses.length - comparaveis.length,
        sitesComMeta: [...new Set(comMeta.map(l => l.site_id_api))],
        temLacuna: meses.some(m => m.diasSemDado > 0),
        temAjusteManual: meses.some(m => m.ajustadoManual),
    };
}

/**
 * Faixas de atingimento. Mais frouxas que as de classificarDesvio de propósito:
 * ali a régua é outra usina no mesmo terreno, no mesmo dia, com o mesmo clima;
 * aqui é um modelo de ano típico simulado em 2023, e o próprio laudo declara
 * 3,1% de variação meteorológica (P90 = 96% do P50). Reprovar por 4% seria
 * reprovar o clima.
 */
export function classificarAtingimento(pct: number | null): StatusDesvio {
    if (pct == null) return 'sem_referencia';
    if (pct >= 100) return 'acima';
    if (pct >= 95) return 'esperado';   // dentro da faixa P90 do laudo
    if (pct >= 85) return 'abaixo';
    return 'critico';
}
